import React, { useEffect, useCallback, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  titleExtra?: React.ReactNode;
  width?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  contentStyle?: React.CSSProperties;
  /** 合并进可滚动内容区(body)的样式。设为 overflow:hidden 可让内部自管滚动，避免双滚动条。 */
  bodyStyle?: React.CSSProperties;
  /**
   * 是否允许用户主动关闭（默认 true）。设为 false 时隐藏右上角 ✕ 并禁用 Esc 关闭，
   * 遮罩本就不响应点击关闭——用于「硬强制」弹窗（如新建项目必须完成的风格/剧本步骤）。
   */
  dismissible?: boolean;
  /**
   * 是否用不透明的主题背景铺满遮罩（默认 false=半透明黑）。设为 true 时遮罩变成
   * 整片不透明背景（随主题白天/黑夜自适应），盖住身后页面——配合 dismissible=false
   * 让硬强制弹窗看起来像一张纯净的设置页。
   *
   * 注意：solidBackdrop 的遮罩从全局顶栏下方（top: var(--header-height)）开始，
   * 不遮挡顶部导航栏——硬强制只锁住"项目内必须按 step"，但不阻挡用户从顶栏离开项目。
   */
  solidBackdrop?: boolean;
}

const Modal: React.FC<ModalProps> = ({ open, onClose, title, titleExtra, width = '520px', children, footer, contentStyle, bodyStyle, dismissible = true, solidBackdrop = false }) => {
  const { t } = useTranslation('common');
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2, 8)}`).current;

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && dismissible) onClose();
  }, [onClose, dismissible]);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setExiting(false);
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.addEventListener('keydown', handleKeyDown);
    } else if (visible) {
      setExiting(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setExiting(false);
      }, 200);
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.removeEventListener('keydown', handleKeyDown);
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleKeyDown, visible]);

  if (!visible) return null;

  return ReactDOM.createPortal(
    <div
      className={`modal-overlay${exiting ? ' modal-overlay-exit' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      // solidBackdrop（硬强制）遮罩从顶栏下方开始，且层级压到顶栏(z-index:100)之下——
      // 顶栏自成层叠上下文，其衍生面板（通知气泡/语种选择/设置面板等）才能正常浮在遮罩之上可点击。
      // 普通模态仍走 .modal-overlay 的 z-index:1000（盖住整屏含顶栏），不受影响。
      style={solidBackdrop ? { background: 'var(--bg)', top: 'var(--header-height)', zIndex: 90 } : undefined}
    >
      <div
        className="modal-content"
        style={{ width, maxWidth: '95vw', ...contentStyle }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 id={titleId} style={{ fontSize: '1.1rem', fontWeight: 600 }}>{title || ''}</h3>
          {titleExtra && <div style={{ marginLeft: 'auto', marginRight: 'var(--space-sm)' }}>{titleExtra}</div>}
          {dismissible && (
            <button
              className="btn btn-ghost"
              onClick={onClose}
              style={{ padding: '0.25rem 0.5rem', fontSize: '1.1rem', lineHeight: 1 }}
              aria-label={t('closeAriaLabel')}
            >
              ✕
            </button>
          )}
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', ...bodyStyle }}>{children}</div>
        {footer && (
          <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexShrink: 0 }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
